const cache = new Map<string, Intl.NumberFormat>();

/**
 * Monedas del modelo que **no** son códigos ISO que `Intl` conozca.
 *
 * La UF es una unidad de cuenta chilena, no una divisa: su código ISO es `CLF`
 * y en Chile se escribe siempre como sufijo —«1.234,5 UF»— nunca con símbolo
 * delante. Pasarle `currency: 'UF'` a `Intl.NumberFormat` **lanza**
 * `RangeError: Invalid currency code`, y como el formateador se construye al
 * renderizar, eso no era un número mal escrito: era la pantalla entera de
 * Negocios en blanco para cualquier cliente que trabaje en UF.
 *
 * Se resuelve con sufijo y no traduciendo `UF`→`CLF` a propósito: `CLF` sale
 * como «CLF 1.234,5», que no es como se escribe acá, y el modelo guarda `UF`
 * —es lo que el cliente eligió y lo que su configuración dice—. La lista es la
 * excepción declarada, así que cualquier moneda nueva pasa por `Intl` salvo que
 * alguien la agregue acá a conciencia.
 */
const SUFFIX_ONLY: Record<string, string> = { UF: 'UF' };

function decimalFormatter(digits: 0 | 1): Intl.NumberFormat {
  const key = `decimal:${digits}`;
  let found = cache.get(key);
  if (!found) {
    found = new Intl.NumberFormat('es-CL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: digits,
    });
    cache.set(key, found);
  }
  return found;
}

function format(value: number, currency: string, digits: 0 | 1): string {
  const suffix = SUFFIX_ONLY[currency];
  if (suffix !== undefined) return `${decimalFormatter(digits).format(value)} ${suffix}`;

  const key = `${currency}:${digits}`;
  let found = cache.get(key);
  if (!found) {
    try {
      found = new Intl.NumberFormat('es-CL', {
        style: 'currency',
        currency,
        // Explicit: for a currency the minimum defaults to the currency's own
        // digits (2 in USD), and a minimum above the maximum throws at runtime.
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
      });
    } catch {
      // Una moneda que `Intl` no conoce y que nadie declaró arriba. Cae al
      // sufijo en vez de tumbar la pantalla: leer «1.234 XYZ» es peor que leer
      // «$1.234», y las dos cosas son infinitamente mejores que no leer nada.
      return `${decimalFormatter(digits).format(value)} ${currency}`;
    }
    cache.set(key, found);
  }
  return found.format(value);
}

const MILLION = 1_000_000;

/** El monto completo, agrupado: `$9.950.000`. */
export const exactMoney = (value: number, currency: string): string =>
  format(value, currency, 0);

/**
 * Millones, escritos como se escriben en Chile.
 *
 * `Intl` compact notation is wrong for money on three counts, all of them
 * visible in the largest figures of the app: it drops the thousands separator
 * once the mantissa passes 999 («$1500 M»), it changes unit mid-column
 * («$10,5 mil M» next to «$5049,4 M»), and it rounds up — printing «$10 M» for
 * a deal of $9.950.000. On an amount, rounding up is not a display detail; it
 * is a different number. So the scaling is ours: exact below a million,
 * truncated to one decimal above it, and always grouped.
 */
export function compactMoney(value: number, currency: string): string {
  if (Math.abs(value) < MILLION) return exactMoney(value, currency);
  const millions = Math.trunc((value / MILLION) * 10) / 10;

  // La «M» va pegada al número, y la unidad después. Con un símbolo delante
  // («$9,9 M») el orden da igual; con una unidad como sufijo no: componer
  // igual que antes escribía «9,9 UF M», que no es una cifra que nadie lea.
  const suffix = SUFFIX_ONLY[currency];
  if (suffix !== undefined) return `${decimalFormatter(1).format(millions)} M ${suffix}`;
  return `${format(millions, currency, 1)} M`;
}

/**
 * Money as a plain string, for the places that cannot take a component: chart
 * axes, tooltips, the table fallback and the exported file.
 *
 * Same arithmetic as `<Money>` on purpose. When the two lived apart, the same
 * amount read «$9,9 M» in a KPI and «$10 M» in the table right underneath it.
 */
export const moneyText = (value: number, currency: string, compact = true): string =>
  compact ? compactMoney(value, currency) : exactMoney(value, currency);
